'use client'

import { Smartphone, Share } from 'lucide-react'

import { Button } from '@/components/ui/button'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog'
import useStorage, { SESSION_STORAGE } from '@/hooks/use-storage'
import { INSTALL_PROMPT_DISMISSED_KEY } from '@/hooks/use-storage/use-storage.constants'
import {
  isIosDevice,
  isAndroidDevice,
  isStandaloneMode,
  canInstallPwa,
} from '@/lib/html/devices'

export default function InstallPromptBanner() {
  const {
    value: promptDismissed,
    setValue: setPromptDismissed,
    storageLoaded: promptStorageLoaded,
  } = useStorage<boolean>(INSTALL_PROMPT_DISMISSED_KEY, false, {
    storageMechanism: SESSION_STORAGE,
  })

  const isStandalone = isStandaloneMode()
  const isIos = isIosDevice()
  const isAndroid = isAndroidDevice()
  const canInstall = canInstallPwa()

  const shouldShowModal =
    !isStandalone &&
    (isIos || isAndroid) &&
    canInstall &&
    !promptDismissed &&
    promptStorageLoaded

  function onClose() {
    setPromptDismissed(true)
  }

  return (
    <Dialog open={shouldShowModal} onOpenChange={open => !open && onClose()}>
      <DialogContent className="border-primary/20 bg-background">
        <DialogHeader className="text-left">
          <DialogTitle className="flex items-center gap-3 text-primary">
            <Smartphone className="size-5" />
            Add GlamApp to your home screen
          </DialogTitle>
          <DialogDescription asChild>
            <div className="text-base leading-relaxed mt-4">
              <div className="flex flex-col gap-4">
                {isIos && (
                  <div>
                    Tap the share button{' '}
                    <span className="inline-flex items-center mx-1 text-dark-placeholder px-1.5 py-0.5 rounded">
                      <Share className="size-3" />
                    </span>{' '}
                    and select &ldquo;Add to Home Screen&rdquo; for the best experience.
                  </div>
                )}
                {isAndroid && (
                  <div>
                    Tap the menu button{' '}
                    <span className="inline-flex items-center mx-1 text-dark-placeholder px-1.5 py-0.5 rounded">
                      ⋮
                    </span>{' '}
                    and select &ldquo;Add to Home Screen&rdquo; or &ldquo;Install App&rdquo;
                    for the best experience.
                  </div>
                )}
                <div className="flex justify-end gap-2 pt-2">
                  <Button
                    variant="primary"
                    size="sm"
                    onClick={onClose}
                    className="flex items-center gap-2"
                  >
                    Got it!
                  </Button>
                </div>
              </div>
            </div>
          </DialogDescription>
        </DialogHeader>
      </DialogContent>
    </Dialog>
  )
}
